/*
 * Module for handling campaign launch action
 */

define([
  "marionette",
  "i18n/i18n",
  "messaging/messenger",
  "./campaignLaunchView",
  "modules/header/kbMenu/kbMenuModel",
  "../campaignScheduleModal/campaignScheduleModalModule",
  "./campaignLaunchModel",
  "../campaignModel",
], function (
  Marionette,
  i18next,
  Messenger,
  CampaignLaunchView,
  KbMenuModel,
  CampaignScheduleModalModule,
  CampaignLaunchModel,
  CampaignModel,
) {
  "use strict";

  return Marionette.Controller.extend({
    initialize: function (options) {
      this.name = "campaignLaunch";
      this.namespace = options.namespace;
      this.region = options.region;
      this._setUpVents();
    },
    show: function (campaign) {
      this.campaign = campaign;
      const mappings = this.campaign.get("mappings") || [];
      this.campaign.assignedResponseCount = mappings ? mappings.length : 0;
      this.allCampaignJobs = campaign.getAllCampaignJobs();
      const kbModel = Messenger.reqres.request("active:kb:model");
      const responseVersioning = kbModel.get("responseVersioning");
      this.versionedKb = responseVersioning;
      if (responseVersioning) {
        campaign.publishOrEnable = i18next.t("admin.campaigns.campaign.publish");
      } else {
        campaign.publishOrEnable = i18next.t("admin.campaigns.campaign.enable");
      }
      campaign.disable = i18next.t("admin.campaigns.campaign.disable");
      campaign.alreadySucceededOnce = campaign.isSuccessful();
      const scheduleTargetStatuses = new Map();
      if (responseVersioning) {
        scheduleTargetStatuses.set(
          KbMenuModel.statuses.VERSIONED_PUBLISHED,
          "admin.campaigns.campaign.schedulePublish",
        );
        scheduleTargetStatuses.set(
          KbMenuModel.statuses.VERSIONED_DISABLED,
          "admin.campaigns.campaign.scheduleDisable",
        );
      } else {
        scheduleTargetStatuses.set(
          KbMenuModel.statuses.NON_VERSIONED_ENABLED,
          "admin.campaigns.campaign.scheduleEnable",
        );
        scheduleTargetStatuses.set(
          KbMenuModel.statuses.NON_VERSIONED_DISABLED,
          "admin.campaigns.campaign.scheduleDisable",
        );
      }
      campaign.scheduleTargetStatuses = scheduleTargetStatuses;
      this.view = new CampaignLaunchView.campaignLaunchView({ model: campaign });

      this.view.on("publish:or:enable:now", this._publishOrEnableNow, this);

      this.view.on("disable:now", this._disableNow, this);

      this.view.on("cancel:schedule", this._cancelSchedule, this);

      this.view.on("open:campaign:schedule:modal", (targetStateId) => {
        this._openCampaignScheduleModal(targetStateId, scheduleTargetStatuses);
      });

      //event for handling opening the status menu
      this.view.on("open:campaign:schedule:status:menu", () => {
        this.view.setStatusMenu(scheduleTargetStatuses);
      });

      this.allCampaignJobs.onSyncedPromise().then(() => {
        this._updateMessagesAndShowVariables();
        this.showRegion(this.view);
      });
    },
    _updateShowLaunchButtons: function () {
      const campaign = this.campaign;
      // show launch buttons if the campaign is not yet successfully run and has at least one response assigned
      campaign.showLaunchButtons =
        campaign.assignedResponseCount > 0 && !campaign.get("finishTimestamp");
      // if campaign is already scheduled or queued or is currently running, then disabled the launch buttons
      campaign.disableLaunchButtons =
        campaign.scheduledCampaign || campaign.enqueuedCampaign || campaign.get("lock");
    },
    _updateMessages: function () {
      this._updateResponseCountMessage();
      this._updateScheduledMessage();
      this._updateCampaignRunningMessage();
      this._updateAlreadySucceededOnceMessage();
      this._updateCampaignFailedMessage();
    },
    _updateScheduledMessage: function () {
      const scheduledCampaign = this._findJob(CampaignModel.JOBRUNR_JOBS_STATUSES.SCHEDULED);
      const enqueuedCampaign = this._findJob(CampaignModel.JOBRUNR_JOBS_STATUSES.ENQUEUED);
      const campaignJob = scheduledCampaign || enqueuedCampaign;
      if (campaignJob) {
        const { action, formattedDate } =
          this._getActionAndFormattedTimeFromCampaignSchedule(campaignJob);
        this.campaign.scheduledMessage = i18next.t("admin.campaigns.campaign.scheduledMessage", {
          action: action,
          user: campaignJob.get("userName"),
          timestamp: formattedDate,
        });
        this.campaign.showScheduledMessage = true;
      } else {
        this.campaign.showScheduledMessage = false;
      }
    },
    _updateCampaignFailedMessage: function () {
      // if campaign is not locked and does not have a finish time and has more than one response still assigned, then it failed.
      const campaign = this.campaign;
      const scheduledCampaign = this._findJob(CampaignModel.JOBRUNR_JOBS_STATUSES.SCHEDULED);
      const enqueuedCampaign = this._findJob(CampaignModel.JOBRUNR_JOBS_STATUSES.ENQUEUED);
      if (scheduledCampaign || enqueuedCampaign) {
        // if campaign is already scheduled or is in queue to run again, then no need to show this message
        campaign.showFailedMessage = false;
        return;
      }
      campaign.failed =
        campaign.get("startTimestamp") &&
        !campaign.get("finishTimestamp") &&
        !campaign.get("lock") &&
        campaign.get("mappings").length > 0;
      const failedCampaign = this._findFailedJob();
      let action;
      let formattedDate;
      if (failedCampaign) {
        const response = this._getActionAndFormattedTimeFromCampaignSchedule(failedCampaign);
        action = response.action;
        formattedDate = response.formattedDate;
      } else {
        action = "Unknown action";
        formattedDate = this._getFormattedDate(campaign.get("startTimestamp"));
      }
      campaign.failedMessage = i18next.t("admin.campaigns.campaign.failedMessage", {
        action: action,
        timestamp: formattedDate,
      });
      campaign.showFailedMessage = campaign.failed;
    },
    _updateResponseCountMessage: function () {
      this.campaign.assignedResponseCountMessage = i18next.t(
        "admin.campaigns.campaign.assignedResponseCountMessage",
        { count: this.campaign.assignedResponseCount },
      );
    },
    _updateCampaignRunningMessage: function () {
      const runningCampaign = this._findJob(CampaignModel.JOBRUNR_JOBS_STATUSES.PROCESSING);
      const enqueuedCampaign = this._findJob(CampaignModel.JOBRUNR_JOBS_STATUSES.ENQUEUED);
      if (this.campaign.get("lock") && runningCampaign) {
        const { action, formattedDate } =
          this._getActionAndFormattedTimeFromCampaignSchedule(runningCampaign);
        this.campaign.lockMessage = i18next.t("admin.campaigns.campaign.lockMessage", {
          action: action,
          count: this.campaign.assignedResponseCount,
        });
        this.campaign.showCampaignRunningMessage = true;
      } else if (enqueuedCampaign) {
        const { action, formattedDate } =
          this._getActionAndFormattedTimeFromCampaignSchedule(enqueuedCampaign);
        this.campaign.lockMessage = i18next.t("admin.campaigns.campaign.lockMessage", {
          action: action,
          count: this.campaign.assignedResponseCount,
        });
        this.campaign.showCampaignRunningMessage = true;
      } else {
        this.campaign.showCampaignRunningMessage = false;
      }
    },
    _updateAlreadySucceededOnceMessage: function () {
      let pastAction;
      let formattedDate;
      let user;
      if (this.campaign.alreadySucceededOnce) {
        if (this.allCampaignJobs) {
          const succeededCampaignJob = this._findJob(CampaignModel.JOBRUNR_JOBS_STATUSES.SUCCEEDED);
          if (succeededCampaignJob) {
            const response =
              this._getPastActionAndFormattedTimeFromCampaignSchedule(succeededCampaignJob);
            pastAction = response.pastAction;
            formattedDate = response.formattedDate;
            user = succeededCampaignJob.get("userName");
          } else {
            pastAction = i18next.t("admin.campaigns.action.run");
            formattedDate = i18next.t("admin.campaigns.campaign.unknownDate");
            user = i18next.t("admin.campaigns.campaign.unknownUser");
          }
        }
        this.campaign.alreadySucceededOnceMessage = i18next.t(
          "admin.campaigns.campaign.alreadySucceededOnceMessage",
          { pastAction: pastAction, user: user, timestamp: formattedDate },
        );
      }
    },
    _findJob: function (jobStatus) {
      return this.allCampaignJobs.findWhere({
        jobStatus: jobStatus,
      });
    },
    _findFailedJob: function () {
      return this._findLatestByStatus(CampaignModel.JOBRUNR_JOBS_STATUSES.FAILED);
    },
    _findLatestByStatus: function (status) {
      const filtered = this.allCampaignJobs.where({ jobStatus: status });
      if (!filtered || filtered.length === 0) {
        return;
      }
      // Initial model with a very old timestamp
      const initialModel = this.allCampaignJobs.createNewCampaignSchedule({
        scheduledTimestamp: CampaignLaunchModel.campaignSchedule.VERY_OLD_TIMESTAMP,
      });

      // get the model with the latest timestamp
      return filtered.reduce((latestModel, currentModel) => {
        const latestTimestamp = new Date(latestModel.get("scheduledTimestamp")).getTime();
        const currentTimestamp = new Date(currentModel.get("scheduledTimestamp")).getTime();

        return latestTimestamp > currentTimestamp ? latestModel : currentModel;
      }, initialModel);
    },
    _getActionAndFormattedTimeFromCampaignSchedule: function (scheduledCampaign) {
      const stateId = scheduledCampaign.get("stateId");
      const timestamp = scheduledCampaign.get("scheduledTimestamp");
      const formattedDate = this._getFormattedDate(timestamp);
      return { action: this._getActionFromStateId(stateId), formattedDate: formattedDate };
    },
    _getFormattedDate: function (timestamp) {
      const dateTime = new Date(timestamp);
      return Messenger.reqres.request("get:formatted:dateTime", dateTime.getTime());
    },
    _getPastActionAndFormattedTimeFromCampaignSchedule: function (scheduledCampaign) {
      const stateId = scheduledCampaign.get("stateId");
      const timestamp = scheduledCampaign.get("scheduledTimestamp");
      const dateTime = new Date(timestamp);
      const formattedDate = Messenger.reqres.request("get:formatted:dateTime", dateTime.getTime());
      return { pastAction: this._getPastActionFromStateId(stateId), formattedDate: formattedDate };
    },
    _getActionFromStateId: function (stateId) {
      switch (stateId) {
        case KbMenuModel.statuses.VERSIONED_PUBLISHED:
          return i18next.t("admin.campaigns.action.publishing");
        case KbMenuModel.statuses.NON_VERSIONED_ENABLED:
          return i18next.t("admin.campaigns.action.enabling");
        case KbMenuModel.statuses.VERSIONED_DISABLED:
        case KbMenuModel.statuses.NON_VERSIONED_DISABLED:
          return i18next.t("admin.campaigns.action.disabling");
        default:
          return i18next.t("admin.campaigns.action.unknown");
      }
    },
    _getPastActionFromStateId: function (stateId) {
      switch (stateId) {
        case KbMenuModel.statuses.VERSIONED_PUBLISHED:
          return i18next.t("admin.campaigns.action.published");
        case KbMenuModel.statuses.NON_VERSIONED_ENABLED:
          return i18next.t("admin.campaigns.action.enabled");
        case KbMenuModel.statuses.VERSIONED_DISABLED:
        case KbMenuModel.statuses.NON_VERSIONED_DISABLED:
          return i18next.t("admin.campaigns.action.disabled");
        default:
          return i18next.t("admin.campaigns.action.unknown");
      }
    },
    _getStatusNameFromStateId: function (stateId) {
      switch (stateId) {
        case KbMenuModel.statuses.VERSIONED_PUBLISHED:
          return i18next.t("topic.statuses.publish");
        case KbMenuModel.statuses.NON_VERSIONED_ENABLED:
          return i18next.t("topic.statuses.enable");
        case KbMenuModel.statuses.VERSIONED_DISABLED:
        case KbMenuModel.statuses.NON_VERSIONED_DISABLED:
          return i18next.t("topic.statuses.disable");
        default:
          return "Unknown status";
      }
    },
    _openCampaignScheduleModal: function (targetStateId, scheduleTargetStatuses) {
      Messenger.vent.trigger(
        "show:modal",
        CampaignScheduleModalModule,
        {},
        {
          campaign: this.campaign,
          targetStateId: targetStateId,
          scheduleTargetStatuses: scheduleTargetStatuses,
        },
        {
          title: "admin.campaigns.campaign.scheduleCampaign",
          closeButton: true,
          closeOnBackgroundClick: false,
          modalWidth: "600px",
          trackInteraction: {
            action: "Campaign Launch",
            id: this.campaign.get("id"),
            idType: "Campaign",
            object: "Campaign",
          },
        },
      );
    },
    _publishOrEnableNow: function () {
      let targetStateId;
      if (this.versionedKb) {
        targetStateId = KbMenuModel.statuses.VERSIONED_PUBLISHED;
      } else {
        targetStateId = KbMenuModel.statuses.NON_VERSIONED_ENABLED;
      }
      this._launchNow(targetStateId);
    },
    _disableNow: function () {
      let targetStateId;
      if (this.versionedKb) {
        targetStateId = KbMenuModel.statuses.VERSIONED_DISABLED;
      } else {
        targetStateId = KbMenuModel.statuses.NON_VERSIONED_DISABLED;
      }
      this._launchNow(targetStateId);
    },
    _launchNow: function (targetStateId) {
      const warningMessage = i18next.t("admin.campaigns.campaign.launchNowWarning", {
        status: this._getStatusNameFromStateId(targetStateId),
        name: this.campaign.get("name"),
      });
      const canOnlyRunOnceMessage = i18next.t("admin.campaigns.campaign.canOnlyRunOnceMessage");
      const timeMessage = i18next.t("admin.campaigns.campaign.timeMessage", {
        action: this._getActionFromStateId(targetStateId).toLowerCase(),
      });
      Messenger.yesNoDialog([warningMessage, canOnlyRunOnceMessage, timeMessage], {
        yes: async () => {
          Messenger.vent.trigger("show:saving:display");
          // launch the campaign immediately
          const user = Messenger.reqres.request("user");
          const campaignLaunchModel = new CampaignLaunchModel.campaignLaunch({
            campaignId: this.campaign.get("id"),
            stateId: targetStateId,
            userId: user.get("userId"),
          });
          try {
            await campaignLaunchModel.saveModelPromise();
            this.allCampaignJobs = this.campaign.getAllCampaignJobs({ forceFetch: true });
            await this.allCampaignJobs.onSyncedPromise();
            this._updateMessagesAndShowVariablesAndReRender();
          } catch (errorMsg) {
            console.error("Could not launch campaign", errorMsg);
            Messenger.okDialog(errorMsg);
          } finally {
            Messenger.vent.trigger("close:saving:display");
          }
        },
      });
    },
    _cancelSchedule: function () {
      // TODO: save campaign model if it is dirty?
      const scheduledCampaign = this._findJob(CampaignModel.JOBRUNR_JOBS_STATUSES.SCHEDULED);
      const { action, formattedDate } =
        this._getActionAndFormattedTimeFromCampaignSchedule(scheduledCampaign);
      const warningMessage = i18next.t("admin.campaigns.campaign.cancelScheduleWarning", {
        action: action.toLowerCase(),
      });
      const scheduleInfo = i18next.t("admin.campaigns.campaign.cancelScheduleInfo", {
        action: action.toLowerCase(),
        user: scheduledCampaign.get("userName"),
        timestamp: formattedDate,
      });
      Messenger.yesNoDialog([warningMessage, scheduleInfo], {
        yes: () => {
          this.campaign
            .clearAllSchedule()
            .then(() => {
              // re-fetch schedule list and re-render layout
              this.allCampaignJobs = this.campaign.getAllCampaignJobs({ forceFetch: true });
              this.allCampaignJobs.onSynced(() => {
                this._updateMessagesAndShowVariablesAndReRender();
              });
            })
            .catch((e) => {
              console.error("Could not clear campaign schedule", e);
              Messenger.okDialog(i18next.t("admin.campaigns.campaign.cancelScheduleError"));
            });
        },
      });
    },
    _updateMessagesAndShowVariablesAndReRender: function () {
      this._updateMessagesAndShowVariables();
      this.view.render();
    },
    _updateMessagesAndShowVariables: function () {
      this.campaign.scheduledCampaign = this._findJob(
        CampaignModel.JOBRUNR_JOBS_STATUSES.SCHEDULED,
      );
      this.campaign.enqueuedCampaign = this._findJob(CampaignModel.JOBRUNR_JOBS_STATUSES.ENQUEUED);
      this._updateShowLaunchButtons();
      this._updateMessages();
    },
    scheduleError: function (errorMessage) {
      this.view.scheduleError(errorMessage);
    },
    _setUpVents: function () {
      Messenger.vent.on("update:launch:area", () => {
        this._updateMessagesAndShowVariablesAndReRender();
      });
    },
  });
});
